/**
 * One reusable confirmation modal for destructive / state-changing actions
 * (Confirm violation, Mark resolved, Dismiss, Suppress).
 *
 * Each action provides:
 *   - title:     short header
 *   - body:      what's about to happen (1-3 sentences, in plain language)
 *   - tone:      'ok' | 'warn' | 'danger' — colors the confirm button
 *   - confirmLabel: button text, e.g. "Mark resolved"
 *   - reasonField: optional. 'optional' | 'required' | 'none'
 *
 * Returns the entered reason via onConfirm. Cancel just closes.
 *
 * Replaces the old pattern of native browser confirm() calls (Suppress)
 * and zero-confirmation transitions (Dismiss / Confirm violation / Mark
 * resolved). Keeps the audit-trail honest: every state change goes
 * through one explicit "are you sure?" with a chance to leave a reason.
 */

import { useState } from 'react';
import { Icon } from './Icon';

export interface ConfirmActionProps {
  title: string;
  body: React.ReactNode;
  tone?: 'ok' | 'warn' | 'danger';
  confirmLabel: string;
  reasonField?: 'none' | 'optional' | 'required';
  reasonPlaceholder?: string;
  /** Show an optional "expires" date input; value (YYYY-MM-DD or null) arrives as onConfirm's 2nd arg. */
  expiresField?: boolean;
  onConfirm: (reason: string, expiresAt: string | null) => Promise<void> | void;
  onClose: () => void;
}

export function ConfirmActionModal({
  title, body, tone = 'warn', confirmLabel,
  reasonField = 'optional', reasonPlaceholder, expiresField = false,
  onConfirm, onClose,
}: ConfirmActionProps) {
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (reasonField === 'required' && !reason.trim()) {
      setError('Reason is required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(reason.trim(), expiresField && expires ? expires : null);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
      setSubmitting(false);
    }
  };

  const tonebg = tone === 'danger' ? 'var(--danger)'
    : tone === 'ok' ? 'var(--ok)'
    : 'var(--accent)';

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--surface)', borderRadius: 8, padding: 22, width: 520,
        border: '1px solid var(--border)', position: 'relative', zIndex: 1101,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}><Icon name="close" size={12} /></button>
        </div>

        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text)', marginBottom: 14 }}>{body}</div>

        {reasonField !== 'none' && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>
              Reason {reasonField === 'optional' ? <span className="muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span> : <span style={{ color: 'var(--danger)' }}>*</span>}
            </label>
            <textarea
              className="textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder ?? 'Notes for the audit trail…'}
              style={{ width: '100%', minHeight: 70, fontSize: 12.5 }}
              autoFocus
            />
          </div>
        )}

        {expiresField && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 4 }}>
              Resolved until <span className="muted" style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
            </label>
            <input
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
              style={{ fontSize: 12.5, padding: '6px 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}
            />
            <div className="muted" style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>
              Leave empty for a permanent skip (until an admin revokes). With a date, the suppression lapses at end of that day — the next audit re-evaluates the triple and re-opens the case if the situation persists.
            </div>
          </div>
        )}

        {error && <div className="c-warn" style={{ fontSize: 12.5, marginBottom: 10 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            className="btn btn-sm"
            onClick={submit}
            disabled={submitting || (reasonField === 'required' && !reason.trim())}
            style={{ background: tonebg, color: '#fff', borderColor: tonebg }}
          >
            {submitting ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
